/* No TikTok requests are made until a visitor explicitly loads one video. */
(() => {
  if (customElements.get('milingi-shop-the-look')) return;

  class MilingiShopTheLook extends HTMLElement {
    #abortController;
    #resizeObserver;
    #activeCard;
    #readyTimeout;

    connectedCallback() {
      if (this.#abortController) return;
      this.#abortController = new AbortController();
      const { signal } = this.#abortController;

      this.querySelectorAll('[data-look-play]').forEach((button) => { button.hidden = false; });
      this.addEventListener('click', this.#onClick, { signal });
      this.addEventListener('keydown', this.#onKeyDown, { signal });
      window.addEventListener('message', this.#onPlayerMessage, { signal });

      const track = this.querySelector('[data-look-track]');
      if (track) {
        track.addEventListener('scroll', this.#updateNavigation, { passive: true, signal });
        this.#resizeObserver = new ResizeObserver(this.#updateNavigation);
        this.#resizeObserver.observe(track);
      }
      this.#updateNavigation();
    }

    disconnectedCallback() {
      this.#abortController?.abort();
      this.#abortController = undefined;
      this.#resizeObserver?.disconnect();
      this.#resizeObserver = undefined;
      this.#closeVideo(false);
    }

    #onClick = (event) => {
      if (!(event.target instanceof Element)) return;
      const play = event.target.closest('[data-look-play]');
      if (play && this.contains(play)) {
        this.#openVideo(play.closest('[data-look-card]'));
        return;
      }
      if (event.target.closest('[data-look-close]')) {
        this.#closeVideo(true);
        return;
      }
      const previous = event.target.closest('[data-look-previous]');
      const next = event.target.closest('[data-look-next]');
      if (previous || next) this.#scrollCards(previous ? -1 : 1);
    };

    #onKeyDown = (event) => {
      if (event.key !== 'Escape' || !this.#activeCard) return;
      event.preventDefault();
      this.#closeVideo(true);
    };

    #onPlayerMessage = (event) => {
      const iframe = this.#activeCard?.querySelector('[data-look-player] iframe');
      if (!iframe || event.origin !== 'https://www.tiktok.com' || event.source !== iframe.contentWindow) return;
      const message = event.data;
      if (!message || typeof message !== 'object' || message['x-tiktok-player'] !== true) return;
      if (message.type === 'onPlayerError') {
        this.#showPlayerFallback('Das Video ist hier gerade nicht verfügbar. Du kannst das Original direkt auf TikTok ansehen.');
      } else if (message.type === 'onPlayerReady' || (message.type === 'onStateChange' && message.value === 1)) {
        window.clearTimeout(this.#readyTimeout);
        const status = this.#activeCard.querySelector('[data-look-status]');
        if (status) status.hidden = true;
      }
    };

    #showPlayerFallback(message) {
      window.clearTimeout(this.#readyTimeout);
      const status = this.#activeCard?.querySelector('[data-look-status]');
      if (!status) return;
      status.textContent = message;
      status.hidden = false;
    }

    #openVideo(card) {
      if (!(card instanceof HTMLElement) || !this.contains(card)) return;
      // Strictly accept original videos from this account; never interpolate arbitrary URLs.
      const match = /^https:\/\/www\.tiktok\.com\/@milingicom\/video\/(\d{18,20})$/.exec(card.dataset.videoUrl || '');
      const player = card.querySelector('[data-look-player]');
      const poster = card.querySelector('[data-look-poster]');
      const play = card.querySelector('[data-look-play]');
      const close = card.querySelector('[data-look-close]');
      const status = card.querySelector('[data-look-status]');
      if (!match || !player || !poster || !play || !close) return;

      this.#closeVideo(false);
      if (status) {
        status.hidden = true;
        status.textContent = '';
      }
      const iframe = document.createElement('iframe');
      iframe.title = `${card.dataset.videoTitle || 'MILINGI'} – TikTok-Video`;
      iframe.src = `https://www.tiktok.com/player/v1/${match[1]}?controls=1&autoplay=0&loop=0&rel=0&description=0&music_info=0`;
      iframe.allow = 'fullscreen; encrypted-media';
      iframe.allowFullscreen = true;
      iframe.referrerPolicy = 'strict-origin-when-cross-origin';
      iframe.addEventListener('error', () => {
        if (this.#activeCard !== card) return;
        this.#closeVideo(true);
        if (status) {
          status.textContent = 'Das Video konnte nicht geladen werden. Du kannst das Original direkt auf TikTok ansehen.';
          status.hidden = false;
        }
      }, { once: true, signal: this.#abortController.signal });

      this.#activeCard = card;
      player.replaceChildren(iframe);
      player.hidden = false;
      poster.hidden = true;
      close.hidden = false;
      play.setAttribute('aria-expanded', 'true');
      close.focus({ preventScroll: true });
      this.#readyTimeout = window.setTimeout(() => {
        // A blocked iframe need not emit an error. Keep its controls usable and offer a visible fallback.
        this.#showPlayerFallback('Video lädt nicht? Du kannst das Original direkt auf TikTok ansehen.');
      }, 15000);
    }

    #closeVideo(restoreFocus) {
      window.clearTimeout(this.#readyTimeout);
      this.#readyTimeout = undefined;
      const card = this.#activeCard;
      if (!card) return;
      const player = card.querySelector('[data-look-player]');
      const poster = card.querySelector('[data-look-poster]');
      const play = card.querySelector('[data-look-play]');
      const close = card.querySelector('[data-look-close]');

      // Removing the iframe stops playback and frees its third-party document.
      player?.replaceChildren();
      if (player) player.hidden = true;
      if (poster) poster.hidden = false;
      if (close) close.hidden = true;
      play?.setAttribute('aria-expanded', 'false');
      this.#activeCard = undefined;
      if (restoreFocus && play instanceof HTMLElement && this.isConnected) play.focus({ preventScroll: true });
    }

    #scrollCards(direction) {
      const track = this.querySelector('[data-look-track]');
      const item = track?.querySelector('.mi-look__item');
      if (!track || !item) return;
      const gap = parseFloat(getComputedStyle(track).columnGap) || 0;
      track.scrollBy({
        left: direction * (item.getBoundingClientRect().width + gap),
        behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
      });
    }

    #updateNavigation = () => {
      const track = this.querySelector('[data-look-track]');
      const navigation = this.querySelector('[data-look-navigation]');
      if (!track || !navigation) return;
      const overflow = track.scrollWidth - track.clientWidth;
      navigation.hidden = overflow <= 2;
      const previous = navigation.querySelector('[data-look-previous]');
      const next = navigation.querySelector('[data-look-next]');
      if (previous) previous.disabled = track.scrollLeft <= 2;
      if (next) next.disabled = track.scrollLeft >= overflow - 2;
    };
  }

  customElements.define('milingi-shop-the-look', MilingiShopTheLook);
})();
